import {
  Autocomplete,
  InputPassword,
  InputText,
  Select,
  ToggleInput,
} from '@ui/src';
import { Controller } from 'react-hook-form';
import { FintracReportSettingsFormTypes } from './types';

import cn from './styles.module.css';

const FintracReportSettingsForm = ({
  control,
  errors,
  currencies,
  thresholdCurrencyId,
}: FintracReportSettingsFormTypes) => (
  <div className={cn.fintracReportSettingsForm}>
    <div className={cn.formGroup}>
      <Select
        name="provider"
        title="Provider"
        placeholder="Select Provider"
        list={[
          {
            name: 'Fintrac',
            value: 'FINTRAC',
          },
        ]}
        value="FINTRAC"
        fullWidth
        disabled
      />
    </div>
    <div className={cn.fintracReportSettingsFormSeparator} />
    <div className={cn.fintracReportSettingsFormTitle}>General settings</div>
    <div className={cn.fintracReportSettingsFormBlock}>
      <div className={cn.formGroup}>
        <Controller
          control={control}
          defaultValue={false}
          name="enabled"
          render={({ field: { onChange, value, name } }) => (
            <ToggleInput
              title="Enable Fintrac reporting"
              name={name}
              checked={value}
              onChange={(e) => onChange(e.target.checked)}
            />
          )}
        />
        {errors?.enabled?.message && (
          <div className={cn.formGroupError}>{errors?.enabled?.message}</div>
        )}
      </div>
      <div className={cn.formGroup}>
        <Controller
          control={control}
          defaultValue={false}
          name="autoSubmit"
          render={({ field: { onChange, value, name } }) => (
            <ToggleInput
              name={name}
              checked={value}
              title="Auto-submit report"
              onChange={(e) => onChange(e.target.checked)}
            />
          )}
        />
        {errors?.autoSubmit?.message && (
          <div className={cn.formGroupError}>{errors?.autoSubmit?.message}</div>
        )}
        <div className={cn.formGroupDescription}>
          EFTR report are sent to FINTRAC automatically once created.
        </div>
      </div>
      <div className={cn.formGroup}>
        <Controller
          control={control}
          defaultValue=""
          name="activitySectorCode"
          render={({ field: { onChange, value, name } }) => (
            <InputText
              title="Activity sector code"
              name={name}
              placeholder="Enter activity sector code"
              value={value as string}
              onChange={onChange}
              error={errors?.activitySectorCode?.message}
            />
          )}
        />
      </div>
      <div className={`${cn.formGroup} ${cn.flex}`}>
        <div className={cn.amountWrapper}>
          <Controller
            control={control}
            name="thresholdAmount"
            defaultValue=""
            render={({ field: { onChange, value, name } }) => (
              <InputText
                title="Reporting threshold"
                name={name}
                placeholder="Enter reporting threshold"
                value={value as string}
                onChange={onChange}
                error={errors?.thresholdAmount?.message}
                currency={currencies?.find(
                  (item) => item.id === thresholdCurrencyId,
                )}
                amountInput
              />
            )}
          />
        </div>
        <div className={cn.currencyWrapper}>
          <Controller
            control={control}
            name="thresholdCurrencyId"
            defaultValue=""
            render={({ field: { onChange, value, name } }) => (
              <Autocomplete
                title="Currency"
                list={currencies?.map((item) => ({
                  name: item.name,
                  value: item.id,
                  description: item.description,
                }))}
                name={name}
                placeholder="Select currency"
                value={value as string}
                onChange={onChange}
                error={errors?.thresholdCurrencyId?.message}
                withClearButton
                fullWidth
              />
            )}
          />
        </div>
      </div>
      <div className={cn.formGroup}>
        <Controller
          control={control}
          defaultValue=""
          name="aggregationCutoffHours"
          render={({ field: { onChange, value, name } }) => (
            <InputText
              title="Window cut-off time"
              name={name}
              placeholder="Enter window cut-off time"
              value={value as string}
              onChange={(e) => {
                let timeValue = e.target.value;

                if (Number(timeValue) > 23) {
                  timeValue = '23';
                } else if (Number(timeValue) < 0) {
                  timeValue = '0';
                }

                onChange(timeValue);
              }}
              error={errors?.aggregationCutoffHours?.message}
            />
          )}
        />
        <div className={cn.formGroupDescription}>
          Hour of day (0–23) when the 24-hour window closes and a new one
          begins.
          <br />
          <b>E.g. 0 = midnight.</b>
        </div>
      </div>
    </div>
    <div className={cn.fintracReportSettingsFormTitle}>Authorization</div>
    <div className={cn.fintracReportSettingsFormBlock}>
      <div className={cn.formGroup}>
        <Controller
          control={control}
          defaultValue=""
          name="baseUrl"
          render={({ field: { onChange, value, name } }) => (
            <InputText
              title="API base URL"
              name={name}
              placeholder="Enter API base URL"
              value={value as string}
              onChange={onChange}
              error={errors?.baseUrl?.message}
            />
          )}
        />
      </div>
      <div className={cn.formGroup}>
        <Controller
          control={control}
          defaultValue=""
          name="clientId"
          render={({ field: { onChange, value, name } }) => (
            <InputText
              title="Client ID"
              name={name}
              placeholder="Enter Client ID"
              value={value as string}
              onChange={onChange}
              error={errors?.clientId?.message}
            />
          )}
        />
      </div>
      <div className={cn.formGroup}>
        <Controller
          control={control}
          defaultValue=""
          name="clientSecret"
          render={({ field: { onChange, value, name } }) => (
            <InputPassword
              title="Secret Key"
              name={name}
              placeholder="Enter Secret Key"
              value={value as string}
              onChange={onChange}
              error={errors?.clientSecret?.message}
              withShowButton
              withCopy
            />
          )}
        />
      </div>
    </div>
  </div>
);

export default FintracReportSettingsForm;
